import moment from 'moment'

import { EventModel } from '@/app/models/newDelegation/eventModel'
import { TripEventType } from '@/app/constants/delegation/tripEventType'
import { TripVehicleType } from '@/app/constants/delegation/tripVehicleType'
import { TmsRouteType } from '@/app/constants/tmsRouteType'
import { determineRouteType } from '@/app/common/tmsRouteTypeHelper'
import { ActivityModel } from '@/modules/drivers/types/activities'
import { withVehicleFromActivities } from './eventVehicle'

export const isTmsEvent = (event: EventModel): boolean =>
  event.tripEvent === TripEventType.Loading ||
  event.tripEvent === TripEventType.Unloading

// The popup edits dates to the minute, markers placed on the chart carry seconds.
const sameMinute = (a: EventModel['date'], b: EventModel['date']): boolean =>
  a == null || b == null ? a == b : moment(a).isSame(moment(b), 'minute')

const documentMarkers = (
  events: EventModel[],
  docNumber: string | undefined,
): EventModel[] =>
  docNumber
    ? events.filter((e) => isTmsEvent(e) && e.docNumber === docNumber)
    : []

const isPair = (items: EventModel[]): boolean =>
  items.length === 2 && items[0].tripEvent !== items[1].tripEvent

const withoutMirror = <T extends EventModel>(event: T): T => ({
  ...event,
  secondDate: null,
  secondCountry: null,
  tmsRouteType: TmsRouteType.Undefined,
})

// Returns the marker itself when it already mirrors `other`, so untouched lists keep their identity.
const mirrored = (
  event: EventModel,
  other: EventModel,
  tmsRouteType: TmsRouteType | undefined,
): EventModel => {
  const type = tmsRouteType ?? event.tmsRouteType
  const upToDate =
    event.tmsRouteType === type &&
    String(event.secondCountry?.id ?? '') === String(other.country?.id ?? '') &&
    (event.secondDate == null || other.date == null
      ? event.secondDate == other.date
      : moment(event.secondDate).isSame(moment(other.date)))
  return upToDate
    ? event
    : {
        ...event,
        secondDate: other.date,
        secondCountry: other.country,
        tmsRouteType: type,
      }
}

// WPF parity (TripViewHelper.UpdateTmsInfo): the two markers of a document mirror each other.
const mirrorPair = (
  [a, b]: EventModel[],
  replace: (from: EventModel, to: EventModel) => void,
): void => {
  // Without nation ids the type would come out as cross-trade for every route.
  const tmsRouteType =
    a.country?.nationId != null && b.country?.nationId != null
      ? determineRouteType(
          a.country,
          b.country,
          a.tripEvent === TripEventType.Loading,
        )
      : undefined
  const nextA = mirrored(a, b, tmsRouteType)
  const nextB = mirrored(b, a, tmsRouteType)
  if (nextA !== a) replace(a, nextA)
  if (nextB !== b) replace(b, nextB)
}

const applyReplacements = <T extends EventModel>(
  events: T[],
  replaced: Map<EventModel, EventModel>,
): T[] =>
  replaced.size ? events.map((e) => (replaced.get(e) as T) ?? e) : events

const relinkDocument = (
  events: EventModel[],
  docNumber: string | undefined,
): EventModel[] => {
  const items = documentMarkers(events, docNumber)
  if (!isPair(items)) return events

  const replaced = new Map<EventModel, EventModel>()
  mirrorPair(items, (from, to) => replaced.set(from, to))
  return applyReplacements(events, replaced)
}

const replaceOne = (
  events: EventModel[],
  target: EventModel,
  change: (event: EventModel) => EventModel,
): EventModel[] => events.map((e) => (e === target ? change(e) : e))

// WPF parity (TripChartPresenter.TimeLineCoordinatorDelegationMarkerChanged): a changed
// second date/country of a loading or unloading adds its counterpart, or moves the existing one.
// Mirrored data is cleared only where this edit broke a pair or removed it by hand: a marker
// loaded without its counterpart has no second data and must keep its stored route type.
export const syncTmsPairOnEdit = (
  events: EventModel[],
  edited: EventModel,
  previous: EventModel | undefined,
  activities: ReadonlyArray<ActivityModel> | undefined,
): EventModel[] => {
  const wasTms = previous != null && isTmsEvent(previous)
  const isTms = isTmsEvent(edited)
  const stayedInDocument =
    wasTms && isTms && previous.docNumber === edited.docNumber
  // The marker `previous` was paired with, if it was half of a pair before this edit.
  const formerOthers = wasTms
    ? documentMarkers(events, previous.docNumber).filter((e) => e !== edited)
    : []
  const formerCounterpart =
    formerOthers.length === 1 &&
    formerOthers[0].tripEvent !== previous?.tripEvent
      ? formerOthers[0]
      : undefined

  let result = events

  if (isTms && edited.docNumber) {
    const otherType =
      edited.tripEvent === TripEventType.Loading
        ? TripEventType.Unloading
        : TripEventType.Loading
    const dateChanged = !sameMinute(previous?.secondDate, edited.secondDate)
    const countryChanged =
      previous?.secondCountry?.id !== edited.secondCountry?.id
    const sameDocument = documentMarkers(result, edited.docNumber)
    const counterpart = sameDocument.find((e) => e.tripEvent === otherType)

    if (
      (dateChanged || countryChanged) &&
      edited.secondDate != null &&
      edited.secondCountry != null
    ) {
      if (counterpart) {
        // A marker that has just joined the document mirrors the counterpart instead of moving it.
        if (stayedInDocument) {
          result = replaceOne(result, counterpart, (e) =>
            withVehicleFromActivities(
              {
                ...e,
                date: dateChanged ? moment(edited.secondDate) : e.date,
                country: countryChanged ? edited.secondCountry : e.country,
              },
              activities,
            ),
          )
        }
      } else if (sameDocument.length < 2) {
        result = [
          ...result,
          withVehicleFromActivities(
            {
              lp: -1,
              tripEvent: otherType,
              date: moment(edited.secondDate),
              country: edited.secondCountry,
              docNumber: edited.docNumber,
              vehicleType: TripVehicleType.NotSet,
              isTransit: false,
              isNew: false,
              temporary: false,
            },
            activities,
          ),
        ]
      }
    }
  }

  const secondRemoved =
    (previous?.secondDate != null && edited.secondDate == null) ||
    (previous?.secondCountry != null && edited.secondCountry == null)
  if (isTms && isPair(documentMarkers(result, edited.docNumber))) {
    result = relinkDocument(result, edited.docNumber)
  } else if (formerCounterpart || secondRemoved) {
    result = replaceOne(result, edited, withoutMirror)
  }

  // The document the marker was in: what is left may have become a pair, or lost one.
  if (wasTms && !stayedInDocument) {
    result = relinkDocument(result, previous.docNumber)
  }
  if (
    formerCounterpart &&
    !isPair(documentMarkers(result, formerCounterpart.docNumber))
  ) {
    result = replaceOne(result, formerCounterpart, withoutMirror)
  }
  return result
}

// Removing one half of a pair clears the other; a marker that merely shared the number is left alone.
export const unlinkRemovedTmsPairs = (
  remaining: EventModel[],
  removed: ReadonlyArray<EventModel>,
): EventModel[] =>
  removed.filter(isTmsEvent).reduce((events, gone) => {
    const left = documentMarkers(events, gone.docNumber)
    return left.length === 1 && left[0].tripEvent !== gone.tripEvent
      ? replaceOne(events, left[0], withoutMirror)
      : relinkDocument(events, gone.docNumber)
  }, remaining)

export const relinkTmsDocuments = <T extends EventModel>(events: T[]): T[] => {
  const byDocument = new Map<string, EventModel[]>()
  for (const event of events) {
    if (!isTmsEvent(event) || !event.docNumber) continue
    const items = byDocument.get(event.docNumber)
    if (items) items.push(event)
    else byDocument.set(event.docNumber, [event])
  }

  const replaced = new Map<EventModel, EventModel>()
  byDocument.forEach((items) => {
    if (isPair(items)) mirrorPair(items, (from, to) => replaced.set(from, to))
  })
  return applyReplacements(events, replaced)
}

const PAIR_FIELDS: ReadonlyArray<keyof EventModel> = [
  'date',
  'country',
  'tripEvent',
  'tmsRouteType',
]

// A row edit in the events table. The document number is typed key by key, so the pair is
// re-linked once, when the cell is left: `baseline` then carries the number it started with.
export const applyEventTableUpdate = <T extends EventModel>(
  events: T[],
  index: number,
  updates: Partial<T>,
  baseline?: Partial<T>,
): T[] => {
  const previous = events[index]
  if (!previous) return events

  const edited = { ...previous, ...updates }
  const updated = events.map((e, i) => (i === index ? edited : e))
  if (!baseline && !PAIR_FIELDS.some((key) => key in updates)) return updated

  return syncTmsPairOnEdit(
    updated,
    edited,
    { ...previous, ...baseline },
    undefined,
  ) as T[]
}
